import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import {
    Card, Stack, Container, Typography, TextField, Alert, Box, Link, CircularProgress, Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 520,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function InvestorSetup2FA() {
    const [searchParams] = useSearchParams();
    const setupToken = searchParams.get('setup_token') || '';
    const [loading, setLoading] = useState(true);
    const [provisioningUri, setProvisioningUri] = useState('');
    const [secret, setSecret] = useState('');
    const [fetchError, setFetchError] = useState('');
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    // Fetch QR provisioning URI
    useEffect(() => {
        if (!setupToken) {
            setFetchError('Missing setup token. Please use the link from your email.');
            setLoading(false);
            return;
        }
        syndicationApi.get(`investor/setup-2fa/?setup_token=${setupToken}`)
            .then((response) => {
                const data = response.data?.data;
                setProvisioningUri(data?.provisioning_uri || '');
                setSecret(data?.secret || '');
            })
            .catch((error) => {
                setFetchError(error.response?.data?.message || 'Failed to load 2FA setup. The link may be expired.');
            })
            .finally(() => setLoading(false));
    }, [setupToken]);

    const ConfirmSchema = Yup.object().shape({
        totp_code: Yup.string()
            .matches(/^[0-9]{6}$/, 'Must be a 6-digit code')
            .required('Enter the code from your authenticator app'),
    });

    const formik = useFormik({
        initialValues: { totp_code: '' },
        validationSchema: ConfirmSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                await syndicationApi.post('investor/setup-2fa/', {
                    setup_token: setupToken,
                    totp_code: values.totp_code,
                });
                setSuccess(true);
            } catch (error) {
                setApiError(error.response?.data?.message || 'Invalid code. Please try again.');
            } finally {
                setSubmitting(false);
            }
        },
    });

    if (success) {
        return (
            <RootStyle title="2FA Setup Complete">
                <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <FormCard>
                        <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">2FA Setup Complete!</Typography>
                            <Alert severity="success" sx={{ width: '100%' }}>
                                Two-factor authentication has been activated. You can now log in.
                            </Alert>
                            <Link component={RouterLink} to="/investor/login" variant="subtitle1">
                                Go to Login
                            </Link>
                        </Stack>
                    </FormCard>
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Setup 2FA | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" gutterBottom>Set Up 2FA</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
                            </Typography>
                        </Box>

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {fetchError && <Alert severity="error">{fetchError}</Alert>}

                        {!loading && !fetchError && (
                            <>
                                {/* QR Code rendered via Google Chart API from provisioning URI */}
                                {provisioningUri && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(provisioningUri)}`}
                                            alt="2FA QR Code"
                                            style={{ width: 200, height: 200, borderRadius: 8 }}
                                        />
                                    </Box>
                                )}

                                {secret && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Can't scan? Enter this key manually:
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                bgcolor: 'grey.100',
                                                px: 2, py: 1, mt: 0.5,
                                                borderRadius: 1,
                                                letterSpacing: 2,
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            {secret}
                                        </Typography>
                                    </Box>
                                )}

                                <Divider />

                                {apiError && <Alert severity="error">{apiError}</Alert>}

                                <FormikProvider value={formik}>
                                    <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                                        <Stack spacing={2.5}>
                                            <TextField
                                                fullWidth
                                                label="6-Digit Verification Code"
                                                placeholder="000000"
                                                inputProps={{
                                                    maxLength: 6,
                                                    style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 },
                                                }}
                                                {...formik.getFieldProps('totp_code')}
                                                error={Boolean(formik.touched.totp_code && formik.errors.totp_code)}
                                                helperText={formik.touched.totp_code && formik.errors.totp_code}
                                            />
                                            <LoadingButton
                                                fullWidth
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                loading={formik.isSubmitting}
                                            >
                                                Verify & Activate
                                            </LoadingButton>
                                        </Stack>
                                    </Form>
                                </FormikProvider>
                            </>
                        )}

                        <Link component={RouterLink} to="/investor/login" variant="body2" sx={{ textAlign: 'center' }}>
                            Back to Login
                        </Link>
                    </Stack>
                </FormCard>
            </Container>
        </RootStyle>
    );
}
